(() => {
  const TAU = Math.PI * 2;
  const FIBERS = 19;
  const SAMPLES = 112;
  const CORE_FIBER = Math.floor(FIBERS / 2);

  const MOBILE_LAYOUTS = [
    { x: 0.73, y: 0.29, width: 0.82, rx: -0.12, ry: 0.1, rz: -0.04 },
    { x: 0.27, y: 0.3, width: 0.8, rx: 0.1, ry: -0.12, rz: 0.03 },
    { x: 0.73, y: 0.29, width: 0.94, rx: -0.08, ry: 0.06, rz: -0.02 },
    { x: 0.29, y: 0.3, width: 0.86, rx: 0.08, ry: -0.08, rz: 0.05 },
    { x: 0.5, y: 0.34, width: 1.12, rx: 0, ry: 0, rz: 0 }
  ];

  const TABLET_LAYOUTS = [
    { x: 0.7, y: 0.31, width: 0.65, rx: -0.1, ry: 0.1, rz: -0.04 },
    { x: 0.3, y: 0.32, width: 0.63, rx: 0.08, ry: -0.11, rz: 0.03 },
    { x: 0.7, y: 0.31, width: 0.74, rx: -0.06, ry: 0.05, rz: -0.02 },
    { x: 0.31, y: 0.32, width: 0.69, rx: 0.07, ry: -0.07, rz: 0.05 },
    { x: 0.5, y: 0.35, width: 1.04, rx: 0, ry: 0, rz: 0 }
  ];

  const DESKTOP_LAYOUTS = [
    { x: 0.73, y: 0.5, width: 0.48, rx: -0.08, ry: 0.09, rz: -0.035 },
    { x: 0.27, y: 0.5, width: 0.46, rx: 0.07, ry: -0.1, rz: 0.025 },
    { x: 0.73, y: 0.5, width: 0.56, rx: -0.05, ry: 0.05, rz: -0.02 },
    { x: 0.28, y: 0.5, width: 0.5, rx: 0.06, ry: -0.06, rz: 0.045 },
    { x: 0.5, y: 0.5, width: 0.94, rx: 0, ry: 0, rz: 0 }
  ];

  const clamp = (value, min = 0, max = 1) => Math.max(min, Math.min(max, value));
  const lerp = (from, to, amount) => from + (to - from) * amount;
  const smootherstep = (value) => {
    const t = clamp(value);
    return t * t * t * (t * (t * 6 - 15) + 10);
  };

  const buildSignalStates = () => {
    const states = Array.from({ length: 5 }, () => new Float32Array(FIBERS * SAMPLES * 3));

    for (let fiber = 0; fiber < FIBERS; fiber += 1) {
      const f = (fiber - CORE_FIBER) / CORE_FIBER;
      const phase = (fiber / FIBERS) * TAU;

      for (let sample = 0; sample < SAMPLES; sample += 1) {
        const t = sample / (SAMPLES - 1);
        const u = t * TAU;
        const index = (fiber * SAMPLES + sample) * 3;

        const closure = TAU * 0.15 * smootherstep((t - 0.75) / 0.25);
        const twist = phase + u * 2.15 - closure;
        const mindRadius = 0.7 + Math.cos(twist) * 0.2;
        states[0][index] = Math.cos(u) * mindRadius;
        states[0][index + 1] = Math.sin(u) * mindRadius;
        states[0][index + 2] = Math.sin(twist) * 0.24 + f * 0.018;

        const globeTilt = phase * 0.55 + f * 0.18;
        states[1][index] = Math.cos(u) * 0.78;
        states[1][index + 1] = Math.sin(u) * Math.cos(globeTilt) * 0.78;
        states[1][index + 2] = Math.sin(u) * Math.sin(globeTilt) * 0.78;

        const tideX = (t - 0.5) * 2.45;
        states[2][index] = tideX;
        states[2][index + 1] = Math.sin(tideX * 2.1 + phase * 0.22) * 0.28 + f * 0.22;
        states[2][index + 2] = Math.cos(tideX * 1.45 - phase * 0.18) * 0.18 + f * 0.06;

        const ensoU = -0.12 + t * TAU * 0.91;
        const ensoRadius = 0.72 + Math.sin(ensoU * 3 + phase * 0.1) * 0.035 + f * 0.075;
        states[3][index] = Math.cos(ensoU) * ensoRadius;
        states[3][index + 1] = Math.sin(ensoU) * ensoRadius;
        states[3][index + 2] = f * 0.14 + Math.sin(ensoU * 2) * 0.05;

        states[4][index] = (t - 0.5) * 3.2;
        states[4][index + 1] = f * 0.008;
        states[4][index + 2] = 0;
      }
    }

    return states;
  };

  const SIGNAL_STATES = buildSignalStates();

  class EnsoSignal extends HTMLElement {
    connectedCallback() {
      if (this.cleanupSignal) return;
      this.cleanupSignal = this.initialize();
    }

    disconnectedCallback() {
      if (this.cleanupSignal) this.cleanupSignal();
      this.cleanupSignal = null;
    }

    initialize() {
      const canvas = this.querySelector(".enso-signal-canvas");
      const hero = this.closest("[data-enso-hero]");
      if (!canvas || !hero) return () => {};

      const root = document.documentElement;
      const context = canvas.getContext("2d", { alpha: true });
      const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      const colorScheme = window.matchMedia("(prefers-color-scheme: light)");
      const designMode = document.body.classList.contains("enso-design-mode");
      const startedAt = performance.now();
      let repeatVisit = designMode;
      let revealTimer = 0;
      let unlockTimer = 0;
      let fallbackTimer = 0;
      let disposed = false;

      if (!designMode) {
        try {
          repeatVisit = window.sessionStorage.getItem("enso-landing-v2-seen:v1") === "1";
        } catch {
          repeatVisit = false;
        }
      }

      root.dataset.signalVisit = repeatVisit ? "repeat" : "first";
      if (!designMode) root.dataset.signalActive = "true";
      root.dataset.canvas = context ? "initializing" : "fallback";
      delete root.dataset.signalReady;

      const reveal = () => {
        if (disposed) return;
        root.dataset.signalReady = "true";
        try {
          window.sessionStorage.setItem("enso-landing-v2-seen:v1", "1");
        } catch {}
        const handoff = reducedMotion || designMode ? 0 : repeatVisit ? 220 : 650;
        unlockTimer = window.setTimeout(() => {
          if (!disposed) delete root.dataset.signalActive;
        }, handoff);
      };

      const scheduleReveal = () => {
        const hold = reducedMotion || designMode ? 0 : repeatVisit ? 0 : 950;
        const remaining = Math.max(0, hold - (performance.now() - startedAt));
        revealTimer = window.setTimeout(reveal, remaining);
      };

      if (!context) {
        scheduleReveal();
        return () => {
          disposed = true;
          window.clearTimeout(revealTimer);
          window.clearTimeout(unlockTimer);
          delete root.dataset.signalActive;
        };
      }

      const projected = new Float32Array(FIBERS * SAMPLES * 2);
      const copyNodes = Array.from(hero.querySelectorAll("[data-hero-copy]"));
      const progressNodes = Array.from(hero.querySelectorAll("[data-hero-progress]"));
      const chapterNodes = Array.from(hero.querySelectorAll("[data-hero-chapter]"));
      const marker = hero.querySelector("[data-dubai-marker]");
      const resolution = hero.querySelector("[data-resolution]");
      const progressHandlers = [];
      let width = 1;
      let height = 1;
      let dpr = 1;
      let frame = 0;
      let targetProgress = 0;
      let visualProgress = 0;
      let lastTime = performance.now();
      let pageVisible = !document.hidden;
      let layouts = MOBILE_LAYOUTS;

      const readPalette = () => {
        const style = getComputedStyle(root);
        return {
          background: style.getPropertyValue("--background").trim() || "#030406",
          silver: style.getPropertyValue("--silver-canvas").trim() || "#aeb7c2",
          silverSoft: style.getPropertyValue("--silver-soft-canvas").trim() || "#77818d",
          accent: style.getPropertyValue("--accent").trim() || "#df4557",
          accentHot: style.getPropertyValue("--accent-hot").trim() || "#fff4f6"
        };
      };

      let palette = readPalette();

      const measure = () => {
        width = window.innerWidth;
        height = window.innerHeight;
        layouts = width >= 1200 || (width >= 900 && width / height >= 1.15)
          ? DESKTOP_LAYOUTS
          : width >= 768
            ? TABLET_LAYOUTS
            : MOBILE_LAYOUTS;
        const cap = width < 768 ? 2.25 : width < 1200 ? 1.75 : 1.5;
        const budget = width < 768 ? 2200000 : width < 1200 ? 4200000 : 5000000;
        dpr = Math.max(1, Math.min(window.devicePixelRatio || 1, cap, Math.sqrt(budget / Math.max(1, width * height))));
        canvas.width = Math.round(width * dpr);
        canvas.height = Math.round(height * dpr);
        canvas.style.width = `${width}px`;
        canvas.style.height = `${height}px`;
        context.setTransform(dpr, 0, 0, dpr, 0, 0);
      };

      const updateTarget = () => {
        const rect = hero.getBoundingClientRect();
        const scrollable = Math.max(1, hero.offsetHeight - height);
        targetProgress = clamp(-rect.top / scrollable);
      };

      const interpolateLayout = (position) => {
        const segment = Math.min(3, Math.floor(position));
        const amount = smootherstep(position - segment);
        const from = layouts[segment];
        const to = layouts[segment + 1];
        return {
          x: lerp(from.x, to.x, amount),
          y: lerp(from.y, to.y, amount),
          width: lerp(from.width, to.width, amount),
          rx: lerp(from.rx, to.rx, amount),
          ry: lerp(from.ry, to.ry, amount),
          rz: lerp(from.rz, to.rz, amount)
        };
      };

      const paint = (now) => {
        const position = clamp(visualProgress * 4, 0, 4);
        const segment = Math.min(3, Math.floor(position));
        const amount = smootherstep(position - segment);
        const layout = interpolateLayout(position);
        const fromState = SIGNAL_STATES[segment];
        const toState = SIGNAL_STATES[segment + 1];
        const centerX = layout.x * width;
        const centerY = layout.y * height;
        const span = Math.min(width, height * 1.08);
        const scale = span * layout.width * 0.48;
        const cosX = Math.cos(layout.rx);
        const sinX = Math.sin(layout.rx);
        const cosY = Math.cos(layout.ry);
        const sinY = Math.sin(layout.ry);
        const cosZ = Math.cos(layout.rz);
        const sinZ = Math.sin(layout.rz);

        context.clearRect(0, 0, width, height);

        for (let fiber = 0; fiber < FIBERS; fiber += 1) {
          for (let sample = 0; sample < SAMPLES; sample += 1) {
            const sourceIndex = (fiber * SAMPLES + sample) * 3;
            const targetIndex = (fiber * SAMPLES + sample) * 2;
            const x = lerp(fromState[sourceIndex], toState[sourceIndex], amount);
            const y = lerp(fromState[sourceIndex + 1], toState[sourceIndex + 1], amount);
            const z = lerp(fromState[sourceIndex + 2], toState[sourceIndex + 2], amount);
            const xY = x * cosY + z * sinY;
            const zY = -x * sinY + z * cosY;
            const yX = y * cosX - zY * sinX;
            const zX = y * sinX + zY * cosX;
            const xZ = xY * cosZ - yX * sinZ;
            const yZ = xY * sinZ + yX * cosZ;
            const perspective = 1 / (1 + zX * 0.22);
            projected[targetIndex] = centerX + xZ * scale * perspective;
            projected[targetIndex + 1] = centerY + yZ * scale * perspective;
          }
        }

        const drawFiber = (fiber, stroke, lineWidth) => {
          context.beginPath();
          for (let sample = 0; sample < SAMPLES; sample += 1) {
            const index = (fiber * SAMPLES + sample) * 2;
            if (sample === 0) context.moveTo(projected[index], projected[index + 1]);
            else context.lineTo(projected[index], projected[index + 1]);
          }
          context.strokeStyle = stroke;
          context.lineWidth = lineWidth;
          context.lineCap = "round";
          context.lineJoin = "round";
          context.stroke();
        };

        for (let fiber = 0; fiber < FIBERS; fiber += 1) {
          drawFiber(fiber, palette.background, fiber === CORE_FIBER ? 4.4 : 3.1);
          const distance = Math.abs(fiber - CORE_FIBER) / CORE_FIBER;
          const stroke = fiber === CORE_FIBER ? palette.accent : distance > 0.64 ? palette.silverSoft : palette.silver;
          drawFiber(fiber, stroke, fiber === CORE_FIBER ? 1.55 : 1.05);
        }

        const pulsePosition = ((now / 42000 + 0.16) % 1) * Math.max(1, SAMPLES - 2);
        const pulseSample = Math.floor(pulsePosition);
        const pulseAmount = pulsePosition - pulseSample;
        const pulseIndexA = (CORE_FIBER * SAMPLES + pulseSample) * 2;
        const pulseIndexB = pulseIndexA + 2;
        const pulseX = lerp(projected[pulseIndexA], projected[pulseIndexB], pulseAmount);
        const pulseY = lerp(projected[pulseIndexA + 1], projected[pulseIndexB + 1], pulseAmount);
        context.beginPath();
        context.arc(pulseX, pulseY, 2.2, 0, TAU);
        context.fillStyle = palette.accentHot;
        context.fill();

        copyNodes.forEach((node, index) => {
          const distance = Math.abs(position - index);
          const opacity = 1 - smootherstep((distance - 0.1) / 0.52);
          node.style.setProperty("--copy-opacity", String(opacity));
          node.style.setProperty("--copy-shift", `${(1 - opacity) * 11}px`);
          node.setAttribute("aria-hidden", opacity < 0.42 ? "true" : "false");
        });

        progressNodes.forEach((node, index) => {
          const fill = clamp(position - index + 0.06);
          node.style.setProperty("--step-progress", String(fill));
          node.dataset.active = String(Math.round(clamp(position, 0, 3)) === index);
        });

        if (marker) {
          const markerOpacity = 1 - smootherstep((Math.abs(position - 1) - 0.06) / 0.2);
          marker.style.setProperty("--marker-opacity", String(markerOpacity));
          marker.style.setProperty("--marker-x", `${centerX + scale * 0.22}px`);
          marker.style.setProperty("--marker-y", `${centerY - scale * 0.42}px`);
        }

        if (resolution) {
          resolution.style.setProperty("--resolution-opacity", String(smootherstep((position - 3.72) / 0.22)));
        }
      };

      const animate = (now) => {
        if (!pageVisible) return;
        const delta = Math.min(0.05, (now - lastTime) / 1000);
        lastTime = now;
        if (reducedMotion) visualProgress = targetProgress;
        else {
          const difference = targetProgress - visualProgress;
          const damping = 2.65 + Math.min(Math.abs(difference) * 8, 2.5);
          visualProgress += difference * (1 - Math.exp(-damping * delta));
        }
        paint(now);
        frame = window.requestAnimationFrame(animate);
      };

      const onResize = () => {
        measure();
        updateTarget();
        paint(performance.now());
      };

      const onScheme = () => {
        palette = readPalette();
        paint(performance.now());
      };

      const onVisibility = () => {
        pageVisible = !document.hidden;
        if (pageVisible) {
          lastTime = performance.now();
          frame = window.requestAnimationFrame(animate);
        } else window.cancelAnimationFrame(frame);
      };

      progressNodes.forEach((node, index) => {
        const handler = () => chapterNodes[index]?.scrollIntoView({ behavior: reducedMotion ? "auto" : "smooth", block: "start" });
        node.addEventListener("click", handler);
        progressHandlers.push([node, handler]);
      });

      fallbackTimer = window.setTimeout(() => {
        if (root.dataset.canvas !== "ready") {
          root.dataset.canvas = "fallback";
          scheduleReveal();
        }
      }, 2200);

      measure();
      updateTarget();
      visualProgress = targetProgress;
      paint(performance.now());
      root.dataset.canvas = "ready";
      window.clearTimeout(fallbackTimer);
      scheduleReveal();

      window.addEventListener("scroll", updateTarget, { passive: true });
      window.addEventListener("resize", onResize, { passive: true });
      colorScheme.addEventListener("change", onScheme);
      document.addEventListener("visibilitychange", onVisibility);
      frame = window.requestAnimationFrame(animate);

      return () => {
        disposed = true;
        window.cancelAnimationFrame(frame);
        window.clearTimeout(revealTimer);
        window.clearTimeout(unlockTimer);
        window.clearTimeout(fallbackTimer);
        window.removeEventListener("scroll", updateTarget);
        window.removeEventListener("resize", onResize);
        colorScheme.removeEventListener("change", onScheme);
        document.removeEventListener("visibilitychange", onVisibility);
        progressHandlers.forEach(([node, handler]) => node.removeEventListener("click", handler));
        delete root.dataset.signalActive;
      };
    }
  }

  if (!customElements.get("enso-signal")) customElements.define("enso-signal", EnsoSignal);

  const validateContent = (scope = document) => {
    scope.querySelectorAll("[data-content-limit]").forEach((node) => {
      const value = node.dataset.contentValue || "";
      const max = Number(node.dataset.contentLimit || 0);
      const used = Array.from(value).length;
      const count = node.querySelector("[data-content-count]");
      if (count) count.textContent = `${used}/${max}`;
      if (max > 0 && used > max) node.dataset.overLimit = "true";
      else delete node.dataset.overLimit;
    });
  };

  const revealContent = (scope = document) => {
    const nodes = Array.from(scope.querySelectorAll("[data-reveal]"));
    document.documentElement.dataset.revealReady = "true";
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      nodes.forEach((node) => { node.dataset.visible = "true"; });
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.dataset.visible = "true";
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: "0px 0px -7%" });
    nodes.forEach((node) => observer.observe(node));
  };

  const initializePage = () => {
    if (!document.querySelector("enso-signal")) document.documentElement.dataset.signalReady = "true";
    validateContent();
    revealContent();
  };

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", initializePage, { once: true });
  else initializePage();

  document.addEventListener("shopify:section:load", (event) => {
    validateContent(event.target);
    revealContent(event.target);
  });

  document.addEventListener("shopify:section:select", (event) => {
    event.target.querySelectorAll("[data-reveal]").forEach((node) => { node.dataset.visible = "true"; });
  });
})();
